import React from 'react';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import { Controller, UseFormMethods } from 'react-hook-form';
import { InputText, InputTextProps } from '../../../components/input';

export type MuiAutocompleteProps<T> = Omit<
  AutocompleteProps<T, any, any, any>,
  'renderInput'
>;

interface RHFAutocompleteProps<T> extends MuiAutocompleteProps<T> {
  control: UseFormMethods['control'];
  errors: UseFormMethods['errors'];
  name: string;
  label: string;
  InputProps?: Omit<InputTextProps, 'name' | 'errors'>;
}

/** Wrapper component providing an interface for a controlled MaterialUI `Autocomplete` component.
 *  [Controller](https://react-hook-form.com/api#Controller) provides an API for creating controlled components
 *  using `react-hook-form`. */
export const RHFAutocomplete = <T, >({
  control,
  errors,
  getOptionLabel,
  name,
  label,
  InputProps,
  ...props
}: RHFAutocompleteProps<T>) => {
  const autocompleteRef = React.useRef<HTMLInputElement | null>(null);

  return (
    <Controller
      render={({ onChange, ...controllerProps }) => (
        <Autocomplete
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (
              typeof option === 'object'
              && option != null
              && getOptionLabel
            ) {
              return getOptionLabel(option as unknown as T);
            }
            return '';
          }}
          renderInput={(params) => (
            <InputText
              {...InputProps}
              {...params}
              name={name}
              errors={errors}
              label={label}
              autoComplete="off"
              ref={autocompleteRef}
            />
          )}
          onChange={(e, data) => onChange(data)}
          {...props}
          {...controllerProps}
        />
      )}
      onFocus={() => {
        autocompleteRef.current?.focus();
      }}
      defaultValue={props.defaultValue == null ? null : props.defaultValue}
      name={name}
      control={control}
    />
  );
};
